import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, ArrowRight, CalendarCheck } from "lucide-react";
import { LeafMotif } from "./NavyogaLogo";
import { scrollToSection } from "./Header";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const yDrift = useTransform(scrollY, [0, 700], [0, 140]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.2]);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#F8FFF8] pt-32 pb-20 lg:pt-44 lg:pb-28">
      <div className="glow-emerald absolute -top-32 -left-32 h-[34rem] w-[34rem] rounded-full" aria-hidden="true" />
      <div className="glow-blue absolute top-1/3 -right-40 h-[38rem] w-[38rem] rounded-full" aria-hidden="true" />

      <motion.div style={{ y: yDrift, opacity: fade }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <span className="font-heading text-outline-blue absolute -right-8 top-16 select-none text-[11rem] font-bold leading-none sm:text-[16rem] lg:text-[21rem]">
          नव
        </span>
        <LeafMotif className="animate-leaf-float absolute left-[8%] top-[18%] h-10 w-10 text-[#2E8B57]/30" />
        <LeafMotif className="animate-leaf-float absolute right-[16%] top-[58%] h-14 w-14 text-[#1E3A8A]/20 [animation-delay:1.8s]" />
        <LeafMotif className="animate-leaf-float absolute left-[38%] bottom-[10%] h-8 w-8 text-[#2E8B57]/25 [animation-delay:3.2s]" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="inline-flex items-center gap-2 rounded-full border border-[#2E8B57]/25 bg-[#2E8B57]/10 px-4 py-2"
          data-testid="hero-experience-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2E8B57] opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#2E8B57]" />
          </span>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#2E8B57]">7+ Years of Experience</span>
        </motion.div>

        <h1 className="font-heading mt-8 max-w-5xl text-4xl font-semibold leading-[1.12] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>Yoga by Navyoga —</MaskedLine>
          <MaskedLine delay={0.4} className="text-[#1E3A8A]">
            7+ Years of Transforming
          </MaskedLine>
          <MaskedLine delay={0.55}>
            Wellness in the <span className="italic text-[#2E8B57]">Andamans.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
        >
          NAVYOGA Yoga and Wellness Center, founded by <span className="font-semibold text-slate-800">Dr. Rajni Ahuja</span>,
          brings 7+ years of experience in Yogic Sciences, Acupuncture and Alternative Therapies to the island
          communities of Port Blair and beyond.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-cta-join-group-classes"
            onClick={() => scrollToSection("#classes")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2E8B57] px-8 py-4 text-sm font-semibold text-white shadow-[0_15px_35px_-12px_rgba(46,139,87,0.55)] transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#267a4b]"
          >
            Join Group Classes
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-book-consultation"
            onClick={() => scrollToSection("#contact")}
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#1E3A8A]/25 bg-white/70 px-8 py-4 text-sm font-semibold text-[#1E3A8A] backdrop-blur transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-[#1E3A8A] hover:bg-white"
          >
            <CalendarCheck className="h-4 w-4" />
            Book a Consultation
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-14 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] text-slate-400"
        >
          <Sparkles className="h-4 w-4 text-[#2E8B57]" />
          Port Blair · Andaman & Nicobar Islands
        </motion.div>
      </div>
    </section>
  );
};
